/**
 * Общая обёртка над fetch: разбирает тело ошибки Payload и бросает ApiError с осмысленным
 * сообщением и статусом. Раньше на любой `!ok` в интерфейс уезжала строка вида
 * `API 401: {"errors":[{"message":"..."}]}`, а на 401 ещё и выполнялся вход-обновление токена
 * с редиректом — из-за этого настоящая причина сбоя (например, блокировка входа после
 * нескольких неверных попыток) до пользователя не доходила.
 */

export class ApiError extends Error {
  /** HTTP-статус ответа. */
  status: number
  /** Текст ошибки от сервера (Payload кладёт его в errors[0].message). */
  serverMessage: string

  constructor(status: number, serverMessage: string) {
    super(serverMessage || `Запрос завершился ошибкой ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.serverMessage = serverMessage
  }
}

function messageOf(body: unknown, fallback: string): string {
  if (typeof body === 'string' && body.trim()) return body.trim()
  const anyBody = body as any
  const first = anyBody?.errors?.[0]
  if (first?.message) {
    const field = Array.isArray(first.path) ? first.path.filter(Boolean).join('.') : first.path
    return field ? `${field}: ${first.message}` : String(first.message)
  }
  if (anyBody?.message) return String(anyBody.message)
  if (anyBody?.error) return String(anyBody.error)
  return fallback
}

export interface ApiRequestOptions {
  /**
   * Не пытаться обновлять токен на 401. Нужно для запроса входа: там 401 означает
   * «неверный пароль» или «вход заблокирован», а не истёкшую сессию.
   */
  skipAuthRetry?: boolean
}

export async function apiRequest<T>(
  path: string,
  init?: RequestInit,
  options?: ApiRequestOptions,
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  if (res.status === 401 && !options?.skipAuthRetry) {
    // Пробуем обновить токен
    const refreshed = await fetch('/api/users/refresh-token', {
      method: 'POST',
      credentials: 'include',
    })

    if (refreshed.ok) {
      // Повторяем оригинальный запрос с новым токеном
      const retry = await fetch(path, {
        ...init,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...init?.headers,
        },
      })
      if (retry.ok) return retry.json() as Promise<T>
    }

    // Refresh не помог — редирект на логин
    window.location.href = '/admin/login'
    return Promise.reject(new Error('Unauthorized'))
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let body: unknown = text
    try {
      body = text ? JSON.parse(text) : null
    } catch {
      // не JSON — оставляем текстом
    }
    throw new ApiError(res.status, messageOf(body, text || res.statusText))
  }

  return res.json() as Promise<T>
}
